import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ADULT_DECLARATION_TEXT } from '../../utils/adultDeclaration';
import {
    confirmAdultVisitor,
    denyUnderageVisitor,
    getAgeGateState,
} from '../../utils/ageGateState';
import ApplicationLoader from '../ApplicationLoader';

const neutralExitUrl = () => {
    const configured = process.env.MIX_UNDER_18_EXIT_URL || 'https://www.google.com/';
    try {
        const url = new URL(configured);
        return url.protocol === 'https:' && url.origin !== window.location.origin
            ? url.href
            : 'https://www.google.com/';
    } catch (error) {
        return 'https://www.google.com/';
    }
};

const gateStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: '#f7f8fa',
};

const panelStyle = {
    maxWidth: '560px',
    width: '100%',
    padding: '32px',
    background: '#fff',
    borderRadius: '10px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
};

const exitUnderage = async () => {
    await axios.post('/api/age-access', { choice: 'denied' });
    denyUnderageVisitor();
    window.location.replace(neutralExitUrl());
};

const AdultAccountDeclaration = ({ children }) => {
    const token = localStorage.getItem('loggedin_access_token');
    const [status, setStatus] = useState(token ? 'loading' : 'guest');
    const [checked, setChecked] = useState(false);
    const [error, setError] = useState('');

    const leaveUnderage = async () => {
        setStatus('saving');
        try {
            await exitUnderage();
        } catch (failure) {
            setStatus('required');
            setError('Unable to record your choice. Please try again.');
        }
    };

    useEffect(() => {
        if (!token) {
            setStatus('guest');
            return;
        }

        let active = true;
        axios.get('/api/adult-self-declaration')
            .then((response) => {
                if (active) setStatus(response.data.declared ? 'declared' : 'required');
            })
            .catch((failure) => {
                if (!active) return;

                if (failure.response?.data?.reason === 'underage_denied') {
                    denyUnderageVisitor();
                    window.location.replace(neutralExitUrl());
                    return;
                }

                setStatus('error');
            });
        return () => { active = false; };
    }, [token]);

    const submit = async (event) => {
        event.preventDefault();
        if (!checked) {
            setError('You must confirm that you are 18 years of age or older.');
            return;
        }
        setStatus('saving');
        setError('');
        try {
            await axios.post('/api/adult-self-declaration', {
                adult_self_declaration_accepted: true,
            });
            setStatus('declared');
        } catch (failure) {
            setStatus('required');
            setError(failure.response?.data?.errors?.adult_self_declaration_accepted?.[0]
                || 'Unable to save your declaration. Please try again.');
        }
    };

    if (status === 'guest' || status === 'declared') return children;
    if (status === 'loading') return <ApplicationLoader />;

    return <main style={gateStyle}>
        <section style={panelStyle} aria-labelledby="adult-account-declaration-heading">
            {status === 'error' ? <>
                <h1 id="adult-account-declaration-heading">Unable to check your age declaration</h1>
                <p>Please refresh this page to try again.</p>
            </> : <>
                <h1 id="adult-account-declaration-heading">Confirm your age for your account</h1>
                <p>Privaposts is available only to people who are 18 years of age or older.</p>
                <form onSubmit={submit}>
                    <label>
                        <input type="checkbox" checked={checked} disabled={status === 'saving'}
                            onChange={(event) => { setChecked(event.target.checked); setError(''); }} />{' '}
                        {ADULT_DECLARATION_TEXT}
                    </label>
                    {error && <p role="alert" className="text-danger">{error}</p>}
                    <div className="mt-3">
                        <button type="submit" className="btn btn-primary mr-2" disabled={status === 'saving'}>Continue</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={leaveUnderage}>I am under 18</button>
                    </div>
                </form>
            </>}
        </section>
    </main>;
};

const AgeGate = ({ children }) => {
    const location = useLocation();
    const isAuthenticated = Boolean(localStorage.getItem('loggedin_access_token'));
    const [gateRevision, setGateRevision] = useState(0);
    const [serverState, setServerState] = useState('loading');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const state = getAgeGateState(
        location.pathname,
        window.sessionStorage,
        window.localStorage,
        isAuthenticated
    );

    useEffect(() => {
        if (state === 'denied') window.location.replace(neutralExitUrl());
    }, [state, location.pathname]);

    useEffect(() => {
        if (isAuthenticated || state === 'exempt' || state === 'denied') return;
        let active = true;
        axios.get('/api/age-access').then((response) => {
            if (!active) return;
            setServerState(response.data.state);
            if (response.data.state === 'denied') {
                denyUnderageVisitor();
                window.location.replace(neutralExitUrl());
            }
        }).catch(() => { if (active) setServerState('error'); });
        return () => { active = false; };
    }, [isAuthenticated, state === 'exempt']);

    const choose = async (choice) => {
        setBusy(true);
        setError('');
        try {
            const response = await axios.post('/api/age-access', { choice });
            if (response.data.state !== choice) throw new Error('Age choice was not recorded.');
            if (choice === 'denied') {
                denyUnderageVisitor();
                window.location.replace(neutralExitUrl());
                return;
            }
            confirmAdultVisitor();
            setServerState('confirmed');
            setGateRevision((revision) => revision + 1);
        } catch (failure) {
            if (failure.response?.data?.state === 'denied') {
                denyUnderageVisitor();
                window.location.replace(neutralExitUrl());
                return;
            }
            setError('Unable to record your choice. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (state === 'denied') return null;
    if (state === 'exempt') return children;
    if (isAuthenticated) return <AdultAccountDeclaration>{children}</AdultAccountDeclaration>;
    if (state === 'confirmed' && serverState === 'confirmed') return <AdultAccountDeclaration key={gateRevision}>{children}</AdultAccountDeclaration>;

    if (serverState === 'loading') return <ApplicationLoader />;
    if (serverState === 'error') return <main style={gateStyle}><p>Unable to check age access. Please refresh this page.</p></main>;
    if (serverState === 'denied') return null;

    return <main style={gateStyle}>
        <section style={panelStyle} aria-labelledby="age-gate-heading">
            <h1 id="age-gate-heading">Privaposts is an age-restricted platform</h1>
            <p>Some content on Privaposts is intended for adults only. You must be 18 years of age or older to enter.</p>
            {error && <p role="alert" className="text-danger">{error}</p>}
            <button type="button" className="btn btn-primary mr-2" disabled={busy} onClick={() => choose('confirmed')}>I am 18 or older</button>
            <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => choose('denied')}>I am under 18</button>
        </section>
    </main>;
};

export default AgeGate;
